import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useHistory } from "react-router-dom";
import { ref, onValue } from "firebase/database";
import { db } from "utils/firebase-config";
import Header from "components/header/Header.container";
import PublicSiteNav from "components/publicSiteNav/PublicSiteNav";
import Footer from "components/footer/Footer";
import Loader from "components/loader/loader";
import InputField from "components/textField/InputField";
import searchIcon from "assets/svg/search.svg";
import {
  Article,
  ARTICLES_LOAD_BATCH_TOUCH,
  ARTICLES_PER_PAGE_DESKTOP,
  isArticleNew,
} from "views/articles/articleTypes";
import { fetchArticleBatch } from "views/articles/fetchArticles";
import CompactMobileSearch from "components/compactMobileSearch/CompactMobileSearch";
import { useCompactMobileSearch } from "hooks/useCompactMobileSearch";
import { isAuthenticated } from "utils/authUtils";
import PageStyles from "./ArticlesPage.module.scss";
import { trackEvent, ClarityEvents, trackSearch } from "utils/clarity";
import { useDateTimeFormat } from "hooks/useDateTimeFormat";
import { formatArticleDisplayDate } from "helpers/dateTimeFormat";

const DESKTOP_QUERY = "(min-width: 1201px)";
const MOBILE_QUERY = "(max-width: 767px)";
const SCROLL_THRESHOLD_PX = 100;
/** When content fits the viewport (e.g. iPad Pro), still allow prefetch without scrolling */
const PAGE_FILL_BUFFER_PX = 50;
/** Prefetch next batch when the 8th card of the current set enters view (10 - 3 = index 7) */
const PREFETCH_CARDS_FROM_END = 3;

type PageDirection = "next" | "prev";

const filterArticles = (articles: Article[], query: string): Article[] => {
  const q = query.trim().toLowerCase();
  if (!q) return articles;

  return articles.filter(
    (article) =>
      article.title.toLowerCase().includes(q) ||
      article.date.toLowerCase().includes(q)
  );
};

const ArticlesPage: FC = () => {
  const history = useHistory();
  const { setting: dateTimeSetting } = useDateTimeFormat();
  const [page, setPage] = useState(0);
  const [allArticles, setAllArticles] = useState<Article[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);

  // Real-time: bump refreshKey when a new article is published so the list
  // re-fetches (no toast). The load effects below depend on refreshKey.
  useEffect(() => {
    const signalRef = ref(db, 'articleUpdates/lastPublishedAt');
    let first = true;
    const unsubscribe = onValue(signalRef, () => {
      if (first) {
        first = false;
        return;
      }
      setRefreshKey(k => k + 1);
    });
    return () => unsubscribe();
  }, []);
  const [direction, setDirection] = useState<PageDirection>("next");
  const [searchQuery, setSearchQuery] = useState("");
  const [isDesktop, setIsDesktop] = useState(
    () => window.matchMedia(DESKTOP_QUERY).matches
  );
  const [touchArticles, setTouchArticles] = useState<Article[]>([]);
  const [touchHasMore, setTouchHasMore] = useState(false);
  const [touchInitialLoading, setTouchInitialLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => window.matchMedia(MOBILE_QUERY).matches
  );
  const prefetchSentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia(DESKTOP_QUERY);
    const handleChange = () => setIsDesktop(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const handleChange = () => setIsMobile(mediaQuery.matches);

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    if (isDesktop) {
      setPage(0);
      let cancelledDesktop = false;
      fetchArticleBatch(0, 500)
        .then(({ articles }) => {
          if (!cancelledDesktop) setAllArticles(articles);
        })
        .catch(() => {});
      return () => {
        cancelledDesktop = true;
      };
    }

    let cancelled = false;

    const loadInitial = async () => {
      setTouchInitialLoading(true);
      setTouchArticles([]);

      try {
        const { articles, hasMore } = await fetchArticleBatch(
          0,
          ARTICLES_LOAD_BATCH_TOUCH
        );
        if (!cancelled) {
          setTouchArticles(articles);
          setTouchHasMore(hasMore);
        }
      } finally {
        if (!cancelled) {
          setTouchInitialLoading(false);
        }
      }
    };

    loadInitial();
    return () => {
      cancelled = true;
    };
  }, [isDesktop, refreshKey]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery]);

  const isSearchActive = searchQuery.trim().length > 0;

  const {
    searchInputRef,
    showCompactSearch,
    isSearchExpanded,
    openMobileSearch,
    handleMobileSearchBlur,
  } = useCompactMobileSearch({ isMobile, isSearchActive });

  const filteredArticles = useMemo(
    () => filterArticles(allArticles, searchQuery),
    [allArticles, searchQuery]
  );
  const effectiveTouchHasMore = !isSearchActive && touchHasMore;

  const loadMoreArticles = useCallback(async () => {
    if (isDesktop || isSearchActive || loadingMoreRef.current || !touchHasMore)
      return;

    loadingMoreRef.current = true;
    setIsLoadingMore(true);

    try {
      const { articles, hasMore } = await fetchArticleBatch(
        touchArticles.length,
        ARTICLES_LOAD_BATCH_TOUCH
      );

      if (articles.length > 0) {
        setTouchArticles((prev) => [...prev, ...articles]);
      }
      setTouchHasMore(hasMore);
    } finally {
      loadingMoreRef.current = false;
      setIsLoadingMore(false);
    }
  }, [isDesktop, isSearchActive, touchArticles.length, touchHasMore]);

  const checkPrefetchLoad = useCallback(() => {
    if (
      isDesktop ||
      isSearchActive ||
      loadingMoreRef.current ||
      !touchHasMore ||
      touchInitialLoading ||
      isLoadingMore
    ) {
      return;
    }

    const sentinel = prefetchSentinelRef.current;
    if (!sentinel) return;

    const rect = sentinel.getBoundingClientRect();
    const isVisible = rect.top < window.innerHeight && rect.bottom > 0;
    if (!isVisible) return;

    const userHasScrolled = window.scrollY >= SCROLL_THRESHOLD_PX;
    const pageFillsViewport =
      document.documentElement.scrollHeight <=
      window.innerHeight + PAGE_FILL_BUFFER_PX;

    if (userHasScrolled || pageFillsViewport) {
      loadMoreArticles();
    }
  }, [
    isDesktop,
    isSearchActive,
    touchHasMore,
    touchInitialLoading,
    isLoadingMore,
    loadMoreArticles,
  ]);

  useEffect(() => {
    if (
      isDesktop ||
      touchInitialLoading ||
      !effectiveTouchHasMore ||
      isLoadingMore
    )
      return undefined;

    const frameId = requestAnimationFrame(() => {
      checkPrefetchLoad();
    });

    return () => cancelAnimationFrame(frameId);
  }, [
    isDesktop,
    touchInitialLoading,
    effectiveTouchHasMore,
    isLoadingMore,
    touchArticles.length,
    checkPrefetchLoad,
  ]);

  useEffect(() => {
    if (
      isDesktop ||
      !effectiveTouchHasMore ||
      touchInitialLoading ||
      isLoadingMore
    )
      return undefined;

    const sentinel = prefetchSentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      () => {
        checkPrefetchLoad();
      },
      { rootMargin: "0px", threshold: 0.25 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    isDesktop,
    effectiveTouchHasMore,
    touchInitialLoading,
    isLoadingMore,
    checkPrefetchLoad,
    touchArticles.length,
  ]);

  useEffect(() => {
    if (isDesktop || !effectiveTouchHasMore || touchInitialLoading)
      return undefined;

    window.addEventListener("scroll", checkPrefetchLoad, { passive: true });
    return () => window.removeEventListener("scroll", checkPrefetchLoad);
  }, [
    isDesktop,
    effectiveTouchHasMore,
    touchInitialLoading,
    checkPrefetchLoad,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredArticles.length / ARTICLES_PER_PAGE_DESKTOP)
  );
  const visible = isDesktop
    ? filteredArticles.slice(
        page * ARTICLES_PER_PAGE_DESKTOP,
        page * ARTICLES_PER_PAGE_DESKTOP + ARTICLES_PER_PAGE_DESKTOP
      )
    : isSearchActive
    ? filteredArticles
    : touchArticles;

  const prefetchCardIndex =
    !isDesktop &&
    effectiveTouchHasMore &&
    touchArticles.length >= PREFETCH_CARDS_FROM_END + 1
      ? touchArticles.length - PREFETCH_CARDS_FROM_END
      : -1;

  const goPrevious = () => {
    setDirection("prev");
    setPage((p) => Math.max(0, p - 1));
    trackEvent(ClarityEvents.ARTICLE_PAGINATION, {
      direction: "prev",
      page: Math.max(0, page - 1),
    });
  };

  const goNext = () => {
    setDirection("next");
    setPage((p) => Math.min(totalPages - 1, p + 1));
    trackEvent(ClarityEvents.ARTICLE_PAGINATION, {
      direction: "next",
      page: Math.min(totalPages - 1, page + 1),
    });
  };

  const gridClassName = isDesktop
    ? `${PageStyles.cardsGrid} ${
        direction === "next"
          ? PageStyles.cardsGridNext
          : PageStyles.cardsGridPrev
      }`
    : PageStyles.cardsGrid;

  const handleSearchChange = (val: string) => {
    setSearchQuery(val);
    if (val.length > 2) {
      const results = filterArticles(allArticles, val);
      trackSearch("articles", val.length, results.length > 0);
    }
  };

  return (
    <div className="pageShell">
      {isAuthenticated() ? <Header /> : <PublicSiteNav />}
      <div className="pageShellMain">
        <div className="custom-container">
          <div className={PageStyles.page}>
            <div className={PageStyles.section}>
              <h1 className={PageStyles.pageTitle}>
                Moderate Politics <br /> Articles &amp; Information
              </h1>

              {isMobile ? (
                <CompactMobileSearch
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onBlur={() => handleMobileSearchBlur(searchQuery)}
                  placeholder="Search Articles"
                  searchIcon={searchIcon}
                  inputClassName={PageStyles.searchField}
                  showCompactSearch={showCompactSearch}
                  isSearchExpanded={isSearchExpanded}
                  searchInputRef={searchInputRef}
                  onOpenMobileSearch={openMobileSearch}
                  openSearchAriaLabel="Open search"
                  focusSearchAriaLabel="Search articles"
                />
              ) : (
                  <div className={`${PageStyles.searchBox} form-group`}>
                    <InputField
                      placeholder="Search Articles"
                      type="text"
                      value={searchQuery}
                      cssClass={PageStyles.searchField}
                      icon={searchIcon}
                      iconClass={PageStyles.searchIcon}
                      handleChange={handleSearchChange}
                    />
                  </div>
                )}

              {!isDesktop && touchInitialLoading && (
                <div className={PageStyles.loadMoreBlock}>
                  <div className={PageStyles.loadMoreSpinner}>
                    <Loader cssClass={PageStyles.loadSpinner} />
                  </div>
                  <p className={PageStyles.loadStatus}>Loading articles…</p>
                </div>
              )}

              <div
                key={isDesktop ? page : isSearchActive ? "search" : "touch"}
                className={gridClassName}
              >
                {visible.map((article, index) => (
                  <div
                    className={`${PageStyles.card} ${article.isRead ? "" : PageStyles.unread}`}
                    key={article.id}
                    ref={
                      index === prefetchCardIndex
                        ? prefetchSentinelRef
                        : undefined
                    }
                    onClick={() => history.push(`/articles/${article.id}`)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        history.push(`/articles/${article.id}`);
                      }
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={`Read article: ${article.title}`}
                  >
                    {isArticleNew(article) && (
                      <span className={PageStyles.newRibbon}>NEW</span>
                    )}
                    <div className={PageStyles.cardImage}>
                      {article.heroImage && <img src={article.heroImage} alt="" />}
                    </div>
                    <div className={PageStyles.cardBody}>
                      <p className={PageStyles.cardTitle}>{article.title}</p>
                      <p className={PageStyles.cardMeta}>
                        {formatArticleDisplayDate(article, dateTimeSetting)}
                        &nbsp;&bull;&nbsp;{article.readTime}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              {visible.length === 0 && !touchInitialLoading && (
                <p className={PageStyles.emptySearch}>No articles found.</p>
              )}

              {!isDesktop && isLoadingMore && (
                <div className={PageStyles.loadMoreBlock}>
                  <div className={PageStyles.loadMoreSpinner}>
                    <Loader cssClass={PageStyles.loadSpinner} />
                  </div>
                  <p className={PageStyles.loadStatus}>Loading more…</p>
                </div>
              )}

              {!isDesktop &&
                !effectiveTouchHasMore &&
                !touchInitialLoading &&
                !isSearchActive &&
                touchArticles.length > ARTICLES_LOAD_BATCH_TOUCH && (
                  <p className={PageStyles.loadEnd}>All articles loaded</p>
                )}

              {isDesktop && (
                <div className={PageStyles.navRow}>
                  <button
                    type="button"
                    className={PageStyles.navBtn}
                    onClick={goPrevious}
                    disabled={page === 0}
                    aria-label="Previous articles"
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className={PageStyles.navBtn}
                    onClick={goNext}
                    disabled={
                      page >= totalPages - 1 || filteredArticles.length === 0
                    }
                    aria-label="Next articles"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ArticlesPage;
